import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography,
  CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const FIELD_STYLE = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [stage, setStage] = useState('loading'); // loading | form | success | error
  const [accountEmail, setAccountEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (mode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then(email => { setAccountEmail(email); setStage('form'); })
        .catch(() => {
          setErrorMsg('Հղումը անվավեր է կամ ժամկետանց: Խնդրում ենք կրկին ուղարկել վերականգնման նամակ:');
          setStage('error');
        });
    } else {
      setErrorMsg('Անճանաչ հրաման: Հղումը կարող է սխալ լինել:');
      setStage('error');
    }
  }, [mode, oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    if (newPassword.length < 6) {
      setFieldError('Գաղտնաբառը պետք է լինի առնվազն 6 նիշ');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError('Գաղտնաբառերը չեն համընկնում');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStage('success');
    } catch {
      setErrorMsg('Սխալ առաջացավ: Հղումը հնարավոր է ժամկետանց է: Խնդրում ենք կրկին փորձել:');
      setStage('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0,
      backgroundColor: '#f9fafb',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      <Box sx={{
        maxWidth: 420, width: '100%',
        backgroundColor: '#fff',
        borderRadius: '16px',
        p: { xs: 3, sm: 4.5 },
        boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/logo/xosumam_logo_transparent_big.png"
            alt="Xosum.am"
            sx={{ height: 56, objectFit: 'contain' }}
          />
        </Box>

        {/* ── LOADING ── */}
        {stage === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={36} thickness={3} sx={{ color: '#4285F4', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">Ստուգում ենք հղումը…</Typography>
          </Box>
        )}

        {/* ── RESET PASSWORD FORM ── */}
        {stage === 'form' && (
          <>
            <Typography variant="h6" fontWeight={700} align="center" sx={{ mb: 0.75, color: '#111827' }}>
              Նոր գաղտնաբառ
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 3.5, color: '#6b7280' }}>
              {accountEmail}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="Նոր գաղտնաբառ"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setFieldError(''); }}
                fullWidth
                required
                size="small"
                autoFocus
                sx={{ ...FIELD_STYLE, mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(v => !v)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Կրկնել գաղտնաբառը"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setFieldError(''); }}
                fullWidth
                required
                size="small"
                sx={{ ...FIELD_STYLE, mb: fieldError ? 1 : 2 }}
              />
              {fieldError && (
                <Typography variant="body2" color="error" sx={{ mb: 2, fontSize: '0.82rem' }}>
                  {fieldError}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{
                  py: 1.4, borderRadius: '10px',
                  fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                  backgroundColor: '#111827', boxShadow: 'none',
                  '&:hover': { backgroundColor: '#1f2937', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                  '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                }}
              >
                {submitting ? <CircularProgress size={18} thickness={3} sx={{ color: '#fff' }} /> : 'Փոխել գաղտնաբառը'}
              </Button>
            </Box>
          </>
        )}

        {/* ── SUCCESS ── */}
        {stage === 'success' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#22c55e', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#111827' }}>
              Գաղտնաբառը փոխվեց
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
              Կարող եք մուտք գործել նոր գաղտնաբառով:
            </Typography>
            <Button
              variant="contained"
              href="/login"
              fullWidth
              sx={{
                py: 1.4, borderRadius: '10px',
                fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                backgroundColor: '#4285F4', boxShadow: 'none',
                '&:hover': { backgroundColor: '#357ae8' },
              }}
            >
              Մուտք գործել
            </Button>
          </Box>
        )}

        {/* ── ERROR ── */}
        {stage === 'error' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#111827' }}>
              Ինչ-որ բան այն չէ
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.7 }}>
              {errorMsg}
            </Typography>
            <Button
              variant="outlined"
              href="/login"
              fullWidth
              sx={{
                py: 1.4, borderRadius: '10px',
                fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                borderColor: '#e5e7eb', color: '#374151',
                '&:hover': { borderColor: '#9ca3af' },
              }}
            >
              Վերադառնալ մուտքի էջ
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuthAction;
